'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { Disc3, LayoutDashboard, Home, Music2, Heart, Sparkles, Mail, User, LogOut, ChevronDown, Menu, X } from 'lucide-react'
import { logout } from '@/app/(auth)/actions'
import BrandWordmark from '@/components/BrandWordmark'

interface NavbarProps {
  user: {
    id: string
    name: string | null
    email: string
    role: string
  } | null
}

export default function Navbar({ user }: NavbarProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isMobileMenuOpen])

  const navLinks = [
    { href: '/', label: 'Events', icon: Home },
    { href: '/songlist', label: 'Songs', icon: Music2 },
    { href: '/wedding-dj', label: 'Wedding DJ', icon: Heart },
    { href: '/spiritual-dj', label: 'Spiritual DJ', icon: Sparkles },
    { href: '/contact', label: 'Contact', icon: Mail },
  ]

  return (
    <>
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed top-0 left-0 right-0 z-50 px-4 pt-4 sm:pt-6"
      >
        <div className="max-w-screen-xl mx-auto">
          <div className="flex items-center justify-between lg:justify-center gap-2 px-3 py-2.5 lg:px-2 lg:py-2 rounded-2xl lg:rounded-full bg-black/70 backdrop-blur-xl border border-white/10 shadow-2xl lg:w-fit lg:mx-auto">
            
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 px-2 sm:px-4 py-2 rounded-full hover:bg-white/5 transition-colors">
              <Disc3 className="w-6 h-6 sm:w-5 sm:h-5 text-neon-pink" />
              <span className="text-base sm:text-sm"><BrandWordmark /></span>
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center">
              <div className="w-px h-6 bg-white/10 mx-1" />
              
              {navLinks.map(link => (
                <Link 
                  key={link.href}
                  href={link.href} 
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition-all text-sm font-medium whitespace-nowrap"
                >
                  <link.icon className="w-4 h-4" />
                  <span>{link.label}</span>
                </Link>
              ))}

              {/* Auth Section - Desktop */}
              {user ? (
                <div className="relative">
                  <button 
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition-all text-sm font-medium"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-neon-purple to-neon-cyan flex items-center justify-center text-xs text-white font-bold uppercase">
                      {user.name?.[0] || 'U'}
                    </div>
                    <span className="max-w-[80px] truncate">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {isProfileOpen && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute top-full right-0 mt-2 w-52 bg-black/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl overflow-hidden flex flex-col p-1.5"
                      >
                        <div className="px-3 py-2 text-xs text-zinc-500 border-b border-white/5 mb-1">
                          Signed in as<br/>
                          <span className="text-white font-medium truncate block">{user.email}</span>
                        </div>

                        {user.role === 'ADMIN' && (
                          <Link 
                            href="/admin" 
                            onClick={() => setIsProfileOpen(false)}
                            className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/10 transition-colors"
                          >
                            <LayoutDashboard className="w-4 h-4" />
                            Dashboard
                          </Link>
                        )}

                        <button 
                          onClick={() => logout()}
                          className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left w-full"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link href="/login" className="flex items-center gap-2 px-4 py-2.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition-all text-sm font-medium">
                  <User className="w-4 h-4" />
                  <span>Sign In</span>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-full hover:bg-white/10 transition-colors text-white"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col h-full pt-24 pb-8 px-6">
              {/* Nav Links */}
              <div className="flex-1 flex flex-col gap-2">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Link 
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-4 px-4 py-4 rounded-xl text-lg font-medium text-white hover:bg-white/10 transition-colors"
                    >
                      <link.icon className="w-6 h-6 text-neon-cyan" />
                      {link.label}
                    </Link>
                  </motion.div>
                ))}

                {user?.role === 'ADMIN' && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Link 
                      href="/admin"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-4 px-4 py-4 rounded-xl text-lg font-medium text-white hover:bg-white/10 transition-colors"
                    >
                      <LayoutDashboard className="w-6 h-6 text-neon-purple" />
                      Dashboard
                    </Link>
                  </motion.div>
                )}
              </div>

              {/* User Section */}
              <div className="border-t border-white/10 pt-6">
                {user ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 px-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-neon-purple to-neon-cyan flex items-center justify-center text-lg text-white font-bold uppercase">
                        {user.name?.[0] || 'U'}
                      </div>
                      <div>
                        <div className="font-medium text-white">{user.name}</div>
                        <div className="text-sm text-zinc-400 truncate max-w-[200px]">{user.email}</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors font-medium"
                    >
                      <LogOut className="w-5 h-5" />
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <Link 
                    href="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-4 rounded-xl bg-gradient-to-r from-neon-pink to-neon-purple text-white font-semibold text-lg"
                  >
                    <User className="w-5 h-5" />
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
